import type { CanvasView, LibraryEntity, ShotView } from "@dramapie/shared";
import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type CSSProperties,
  type KeyboardEvent as ReactKeyboardEvent,
  type MouseEvent as ReactMouseEvent,
  type PointerEvent as ReactPointerEvent,
  type ReactNode,
} from "react";

import {
  buildCanvasGraph,
  canvasLayoutKey,
  clearCanvasLayout,
  computeCanvasImpact,
  edgePath,
  edgesTouching,
  fitCanvasContent,
  moveNodeInLayout,
  readCanvasLayout,
  relationEdgePath,
  relationOverlayFor,
  shotNodeId,
  UNPLACED_SCENE_ID,
  writeCanvasLayout,
  CANVAS_ASSET_KIND_LABEL,
  CANVAS_RELATION_OFFSET,
  type CanvasEdge,
  type CanvasLayout,
  type CanvasNode,
  type CanvasNodeRect,
  type CanvasRelationItem,
} from "../../lib/canvasLayout";
import { formatDuration } from "../../lib/format";
import { libraryRefUrl } from "../../lib/m5";
import { centerCameraOn, clampZoom, zoomAtPoint, ZOOM_STEP, type Camera } from "../../lib/m6";
import { SHOT_STATUS_META, shortId } from "../../lib/status";
import { EmptyState } from "../states";

export interface CanvasBoardProps {
  data: CanvasView;
  /** 当前选中镜：画布卡的琥珀描边与右栏共用同一份状态。 */
  activeShotId: string | null;
  onSelectShot: (shotId: string) => void;
  /** 「在画布看」：挂载后把这一镜移到视口中央。 */
  focusShotId?: string | null;
  /**
   * 资料库实体（`GET /library`，kind=all）。
   * 传了才画角色 / 场景 / 道具节点与 references 边；不传则只有场块 + 镜头卡。
   */
  library?: readonly LibraryEntity[];
  /** 已加入生成队列的镜头（Shift+点击镜头卡切换；只影响描边，不写 shot.order）。 */
  queuedShotIds?: readonly string[];
  onToggleQueueShot?: (shotId: string) => void;
  /**
   * 画布右上角的生成队列浮层（由主 agent 传 `<GenerationQueue/>`）。
   * CanvasBoard 只负责把它放进画布坐标系的右上角，不关心队列内部。
   */
  queueSlot?: ReactNode;
  /** 右键菜单：查看版本（C-11）。 */
  onOpenVersions?: (shotId: string) => void;
  /** 右键菜单：查看首尾帧（镜头卡 / 首帧卡 / 出片卡都走这里）。 */
  onOpenFrames?: (shotId: string) => void;
  /**
   * 右键菜单：打开成片（出片卡）。
   *
   * 页面没传就不显示这一项（不造死菜单）。页面接线：
   * ```tsx
   * <CanvasBoard … onOpenClip={(shotId) => { setClipShotId(shotId); setOutputOpen(true); }} />
   * ```
   */
  onOpenClip?: (shotId: string) => void;
  /** 右键菜单 / 双击：在故事板打开。 */
  onOpenStoryboard?: (shotId: string) => void;
  /** 右键菜单 / 影响面浮条：打开资料库。 */
  onOpenLibrary?: (target: { kind: LibraryEntity["kind"]; id: string }) => void;
  /**
   * 画布图例里的「⌘ 快捷操作」入口（原型 `canvas-shortcuts-btn`）。
   * 页面接线：`onOpenShortcuts={() => setShortcutsOpen(true)}`（页面已有 ShortcutsSheet）。
   */
  onOpenShortcuts?: () => void;
  /** 本地布局 sidecar 的 key 覆盖；默认按 project + episode。 */
  layoutKey?: string;
}

interface MenuItem {
  action: "queue" | "versions" | "frames" | "clip" | "storyboard" | "impact" | "library" | "focus";
  label: string;
}

interface ContextMenuState {
  nodeId: string;
  x: number;
  y: number;
}

const LEGEND = [
  { tone: "approved", label: "确认" },
  { tone: "review", label: "复核" },
  { tone: "pending", label: "待生成" },
  { tone: "error", label: "失败" },
] as const;

const EDGE_MARKER: Record<CanvasEdge["kind"], string> = {
  sequence: "dp-arr-seq",
  derives: "dp-arr-der",
  references: "dp-arr-ref",
};

const pad2 = (value: number): string => String(value).padStart(2, "0");

/** 关联缩略图点完之后，目标节点高亮多久。 */
const RELATION_HIGHLIGHT_MS = 1400;

/** hover 提示词卡的宽度（世界坐标）；右边放不下时翻到卡片左边。 */
const SHOT_HOVER_WIDTH = 248;
/** 卡片 → 提示词卡的水平间距（留一点缝，不压住卡片本身）。 */
const SHOT_HOVER_GAP = 10;
/**
 * 移开后的收起延迟：鼠标要从卡片滑进浮卡里点「复制」，
 * 立刻收起的话浮卡会在鼠标到达之前消失（跟 tooltip 同款问题）。
 */
const SHOT_HOVER_HIDE_MS = 160;
/** 浮卡的估算高度（屏幕像素）：只用来在卡片贴视口底边时把浮卡上提，避免被裁。 */
const SHOT_HOVER_ESTIMATE_HEIGHT = 300;
/** 浮卡 id：镜头卡的 `aria-describedby` 指向它。 */
const SHOT_HOVER_ID = "dp-canvas-shot-hover";

/** 首帧 / 出片卡也代表一个镜头（右键菜单、整组拖动都要它）。 */
function nodeShot(node: CanvasNode): ShotView | null {
  if (node.kind === "shot" || node.kind === "keyframe" || node.kind === "clip") return node.shot;
  return null;
}

function readSize(element: HTMLElement | null): { width: number; height: number } {
  return { width: element?.clientWidth ?? 0, height: element?.clientHeight ?? 0 };
}

function rectStyle(node: CanvasNodeRect): CSSProperties {
  return { left: node.x, top: node.y, width: node.w, height: node.h };
}

function isEditableTarget(target: EventTarget | null): boolean {
  if (!(target instanceof HTMLElement)) return false;
  return target.closest("button, a, input, select, textarea, label, [role='menu']") !== null;
}

/** 拖动成员：拖场块标题 = 整组（场 + 镜头 + 该场镜头的首帧/出片）；其它节点只动自己。 */
function dragMembers(node: CanvasNode, nodes: readonly CanvasNode[]): CanvasNode[] {
  if (node.kind !== "scene") return [node];
  // 派生卡自己不带 sceneId（只有 shot），未分场（`__unplaced__`）时不能拿 shot.sceneId
  // 去比对 —— 先按画布上的镜头卡反查它实际挂在哪一组。
  const shotGroup = new Map<string, string>();
  for (const item of nodes) {
    if (item.kind === "shot") shotGroup.set(item.shot.id, item.sceneId);
  }
  return nodes.filter(
    (item) =>
      item.id === node.id ||
      (item.kind === "shot" && item.sceneId === node.sceneId) ||
      ((item.kind === "keyframe" || item.kind === "clip") &&
        shotGroup.get(item.shot.id) === node.sceneId),
  );
}

type FullscreenDocument = Document & {
  fullscreenElement?: Element | null;
  exitFullscreen?: () => Promise<void>;
  webkitFullscreenElement?: Element | null;
  webkitExitFullscreen?: () => Promise<void>;
};

type FullscreenElement = HTMLElement & {
  requestFullscreen?: () => Promise<void>;
  webkitRequestFullscreen?: () => Promise<void>;
};

/**
 * 画布（契约 M6 §1 + 原型 C-01…C-16）。
 *
 * 服务端给的是「场容器 rect + 镜头 rect」，前端**不重算布局**：
 * 场画成虚线容器，卡按 rect 绝对定位；平移 / 缩放只改变 world 的 transform。
 * 在这之上补齐：
 * - 连接线（sequence / derives / references）与选中高亮（C-03 / C-04）；
 * - 素材节点（角色 / 场景 / 道具）与派生节点（首帧 / 出片）（C-01）；
 * - 拖动节点 / 场块整组 + localStorage sidecar（C-07 / C-08）；
 * - 滚轮平移、Shift 横移、Ctrl/⌘ 缩放（C-09）；
 * - 全屏（C-10）、右键菜单（C-11）、影响面（C-05）；
 * - 镜头卡 hover 提示词浮卡（原型 `shot-cover-hover`，数据是投影里的 `promptPreview`）。
 */
export function CanvasBoard({
  data,
  activeShotId,
  onSelectShot,
  focusShotId,
  library,
  queuedShotIds,
  onToggleQueueShot,
  queueSlot,
  onOpenVersions,
  onOpenFrames,
  onOpenClip,
  onOpenStoryboard,
  onOpenLibrary,
  onOpenShortcuts,
  layoutKey: layoutKeyProp,
}: CanvasBoardProps) {
  const shellRef = useRef<HTMLDivElement | null>(null);
  const menuRef = useRef<HTMLDivElement | null>(null);
  const hoverPanelRef = useRef<HTMLDivElement | null>(null);
  const panRef = useRef<{
    pointerId: number;
    startX: number;
    startY: number;
    originX: number;
    originY: number;
    moved: boolean;
  } | null>(null);
  const nodeDragRef = useRef<{
    pointerId: number;
    startX: number;
    startY: number;
    moved: boolean;
    items: Array<{ id: string; x: number; y: number }>;
  } | null>(null);
  const suppressClickRef = useRef(false);
  /** 用户是否动过相机（平移 / 缩放 / 跳转）：动过之后自动「适应」永久退场。 */
  const interactedRef = useRef(false);
  const episodeRef = useRef(data.episode.id);
  const relationHighlightTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const layoutKey = layoutKeyProp ?? canvasLayoutKey(data.projectId, data.episode.id);
  const layoutKeyRef = useRef(layoutKey);
  const [layout, setLayout] = useState<CanvasLayout>(() => readCanvasLayout(layoutKey));
  const layoutRef = useRef<CanvasLayout>(layout);

  const [camera, setCamera] = useState<Camera>(() => ({
    x: data.viewport.x,
    y: data.viewport.y,
    zoom: clampZoom(data.viewport.zoom),
  }));
  const [panning, setPanning] = useState(false);
  const [draggingNode, setDraggingNode] = useState(false);
  const [impactNodeId, setImpactNodeId] = useState<string | null>(null);
  const [menu, setMenu] = useState<ContextMenuState | null>(null);
  const [isFullscreen, setIsFullscreen] = useState(false);
  /** C-06：点完关联缩略图后短时高亮的节点 id。 */
  const [relationTargetId, setRelationTargetId] = useState<string | null>(null);
  /** hover 中的镜头 id：浮卡显示它的 `promptPreview`（demo `shot-cover-hover`）。 */
  const [hoveredShotId, setHoveredShotId] = useState<string | null>(null);
  /** 复制提示词的反馈：idle → done / fail，1.4s 后回 idle。 */
  const [copyState, setCopyState] = useState<"idle" | "done" | "fail">("idle");
  const hoverHideTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const copyResetTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    layoutRef.current = layout;
  }, [layout]);

  // 换集 / 换项目：布局 sidecar 跟着换 key，旧的高亮与菜单不能跨集残留。
  // hover 浮卡同理：它引用的是上一集的镜头，换集后必须收掉。
  useEffect(() => {
    if (layoutKeyRef.current === layoutKey) return;
    layoutKeyRef.current = layoutKey;
    const next = readCanvasLayout(layoutKey);
    layoutRef.current = next;
    setLayout(next);
    setImpactNodeId(null);
    setMenu(null);
    setHoveredShotId(null);
  }, [layoutKey]);

  const graph = useMemo(
    () => buildCanvasGraph({ data, library, layout }),
    [data, library, layout],
  );
  const nodeById = useMemo(
    () => new Map<string, CanvasNode>(graph.nodes.map((node) => [node.id, node])),
    [graph.nodes],
  );
  const queued = useMemo(() => new Set(queuedShotIds ?? []), [queuedShotIds]);

  /** 当前选中的镜头（画布上真有的那一个）；右栏选中别的集的镜头时为 null。 */
  const activeShot = useMemo(() => {
    if (!activeShotId) return null;
    const node = nodeById.get(shotNodeId(activeShotId));
    return node && node.kind === "shot" ? node.shot : null;
  }, [activeShotId, nodeById]);

  /** C-06：选中镜头右侧的关联缩略图；没选中 / 凑不出缩略图时为 null（整块消失）。 */
  const relation = useMemo(
    () =>
      relationOverlayFor({
        shot: activeShot,
        nodes: graph.nodes,
        library,
        // 贴到板右边缘时翻到左边（原型同款），不把缩略图甩到空白里。
        boundsRight: graph.bounds.x + graph.bounds.w,
      }),
    [activeShot, graph.bounds, graph.nodes, library],
  );

  /** hover 浮卡的落点：卡片右边（被关联缩略图占了就再往右让），右边放不下就翻到左边。 */
  const hoveredNode = hoveredShotId ? nodeById.get(shotNodeId(hoveredShotId)) : null;
  const hoverPanel = useMemo(() => {
    if (!hoveredNode || hoveredNode.kind !== "shot") return null;
    // 浮卡按**屏幕尺寸**画（`scale(1/zoom)`）：画布默认「适应」到 0.3–0.4 时，
    // 跟着世界缩放的 9px 提示词会变成 3px 的噪点，等于没做。
    const zoom = camera.zoom > 0 ? camera.zoom : 1;
    const size = readSize(shellRef.current);
    const relationsPx =
      relation && relation.anchorId === hoveredNode.id
        ? relation.rect.w + CANVAS_RELATION_OFFSET
        : 0;
    const anchorRightScreen = camera.x + (hoveredNode.x + hoveredNode.w + relationsPx) * zoom;
    const fitsRight =
      size.width <= 0 || anchorRightScreen + SHOT_HOVER_GAP + SHOT_HOVER_WIDTH <= size.width;
    const left = fitsRight
      ? hoveredNode.x + hoveredNode.w + (relationsPx + SHOT_HOVER_GAP) / zoom
      : hoveredNode.x - (SHOT_HOVER_GAP + SHOT_HOVER_WIDTH) / zoom;
    // 卡片贴到视口底边时把浮卡上提（估算高度，只为了避免被 overflow: hidden 裁掉）。
    const cardTopScreen = camera.y + hoveredNode.y * zoom;
    const maxTopScreen =
      size.height > 0 ? Math.max(8, size.height - SHOT_HOVER_ESTIMATE_HEIGHT) : cardTopScreen;
    const topScreen = Math.min(Math.max(cardTopScreen, 8), maxTopScreen);
    return {
      shot: hoveredNode.shot,
      left,
      top: (topScreen - camera.y) / zoom,
      scale: 1 / zoom,
    };
  }, [camera, hoveredNode, relation]);

  const hasShots = graph.nodes.some((node) => node.kind === "shot");

  const impact = useMemo(() => {
    if (!impactNodeId) return null;
    const node = nodeById.get(impactNodeId);
    if (!node || node.kind !== "asset") return null;
    return computeCanvasImpact({
      data,
      target: {
        kind: node.entity.kind,
        entityId: node.entity.id,
        name: node.entity.name,
        nodeId: node.id,
      },
    });
  }, [data, impactNodeId, nodeById]);

  const impactShotIds = useMemo(() => new Set(impact?.shotIds ?? []), [impact]);

  /** 选中节点 / 影响面 → 要亮的边；null = 不做高亮（全部按常态画）。 */
  const litEdgeIds = useMemo<Set<string> | null>(() => {
    if (impact) {
      const ids = new Set<string>([impact.target.nodeId]);
      for (const shotId of impact.shotIds) ids.add(shotNodeId(shotId));
      return new Set(edgesTouching(graph.edges, ids).map((edge) => edge.id));
    }
    const selected = activeShotId ? shotNodeId(activeShotId) : null;
    // 右栏选中的镜头可能属于别的集：找不到节点就不做「全部压暗」，否则整张画布会灰掉。
    if (!selected || !nodeById.has(selected)) return null;
    return new Set(edgesTouching(graph.edges, new Set([selected])).map((edge) => edge.id));
  }, [activeShotId, graph.edges, impact, nodeById]);

  /** 世界坐标里的 SVG 画布（拖到负坐标、关联缩略图溢出右边都要能画）。 */
  const svgBox = useMemo(() => {
    const rects: CanvasNodeRect[] = [graph.bounds];
    if (relation) rects.push(relation.rect);
    let left = 0;
    let top = 0;
    let right = 0;
    let bottom = 0;
    for (const rect of rects) {
      left = Math.min(left, rect.x);
      top = Math.min(top, rect.y);
      right = Math.max(right, rect.x + rect.w);
      bottom = Math.max(bottom, rect.y + rect.h);
    }
    return {
      left: left - 60,
      top: top - 60,
      width: Math.max(1, right - left + 120),
      height: Math.max(1, bottom - top + 120),
    };
  }, [graph.bounds, relation]);

  const focusRect = useMemo<CanvasNodeRect | null>(() => {
    if (!focusShotId) return null;
    const node = nodeById.get(shotNodeId(focusShotId));
    if (!node || node.kind !== "shot") return null;
    return { x: node.x, y: node.y, w: node.w, h: node.h };
  }, [focusShotId, nodeById]);

  /**
   * 「适应画面」框的是**所有节点**的并集（场块 + 镜头卡 + 素材卡 + 派生卡）：
   * 只框场块会让素材带落在视口外（线上「内容偏下、素材被切在底部」）。
   */
  const fit = useCallback(() => {
    interactedRef.current = true;
    const next = fitCanvasContent(graph.bounds, readSize(shellRef.current));
    if (next) setCamera(next);
  }, [graph.bounds]);

  const zoomBy = useCallback((factor: number) => {
    interactedRef.current = true;
    const size = readSize(shellRef.current);
    setCamera((prev) =>
      zoomAtPoint(prev, prev.zoom * factor, { x: size.width / 2, y: size.height / 2 }),
    );
  }, []);

  const centerOn = useCallback((rect: CanvasNodeRect) => {
    interactedRef.current = true;
    const size = readSize(shellRef.current);
    setCamera((prev) =>
      size.width > 0 && size.height > 0
        ? centerCameraOn(rect, prev.zoom, size)
        : { ...prev, x: -rect.x * prev.zoom, y: -rect.y * prev.zoom },
    );
  }, []);

  /** C-06：点关联缩略图 → 目标节点滚到视口中央 + 短时高亮。 */
  const jumpToRelation = useCallback(
    (item: CanvasRelationItem) => {
      const node = nodeById.get(item.nodeId);
      if (!node) return;
      centerOn(node);
      setRelationTargetId(item.nodeId);
      if (relationHighlightTimer.current) clearTimeout(relationHighlightTimer.current);
      relationHighlightTimer.current = setTimeout(
        () => setRelationTargetId(null),
        RELATION_HIGHLIGHT_MS,
      );
    },
    [centerOn, nodeById],
  );

  useEffect(
    () => () => {
      if (relationHighlightTimer.current) clearTimeout(relationHighlightTimer.current);
      if (hoverHideTimer.current) clearTimeout(hoverHideTimer.current);
      if (copyResetTimer.current) clearTimeout(copyResetTimer.current);
    },
    [],
  );

  // ---------------------------------------------------------------- hover 提示词（原型 shot-cover-hover）

  const cancelHoverHide = useCallback(() => {
    if (hoverHideTimer.current) clearTimeout(hoverHideTimer.current);
    hoverHideTimer.current = null;
  }, []);

  const showShotHover = useCallback(
    (shotId: string) => {
      cancelHoverHide();
      setHoveredShotId(shotId);
      setCopyState("idle");
    },
    [cancelHoverHide],
  );

  const scheduleHoverHide = useCallback(() => {
    cancelHoverHide();
    hoverHideTimer.current = setTimeout(() => setHoveredShotId(null), SHOT_HOVER_HIDE_MS);
  }, [cancelHoverHide]);

  /**
   * 复制提示词。没有 clipboard（非安全上下文 / 老浏览器）就退回选中文本：
   * 提示词是能选中的 `<pre>`，用户至少还能手动复制。
   */
  const copyPrompt = useCallback(async (text: string) => {
    const clipboard = typeof navigator === "undefined" ? undefined : navigator.clipboard;
    let ok = false;
    if (clipboard?.writeText) {
      try {
        await clipboard.writeText(text);
        ok = true;
      } catch {
        ok = false;
      }
    }
    setCopyState(ok ? "done" : "fail");
    if (copyResetTimer.current) clearTimeout(copyResetTimer.current);
    copyResetTimer.current = setTimeout(() => setCopyState("idle"), 1400);
  }, []);

  /**
   * 自动「适应」：只在用户碰相机之前生效（打开画布 / 数据或素材带后到时重算一次）。
   *
   * - 视口尺寸为 0（还没布局 / 隐藏容器 / 切视图动画）时返回 false：
   *   留给 ResizeObserver 在拿到真实尺寸后补一次，否则相机就停在上一次的位置；
   * - 用户一旦平移 / 缩放 / 跳转（`interactedRef`），这里永久退场，不再打扰。
   */
  const autoFit = useCallback((): boolean => {
    if (interactedRef.current) return true;
    const shell = shellRef.current;
    const size = readSize(shell);
    if (!shell || size.width <= 0 || size.height <= 0) return false;
    if (focusRect) {
      setCamera((prev) => centerCameraOn(focusRect, prev.zoom, size));
      return true;
    }
    const next = fitCanvasContent(graph.bounds, size);
    if (next) setCamera(next);
    return true;
  }, [focusRect, graph.bounds]);

  // 挂载后先试一次；尺寸还没出来就等 ResizeObserver 的首次回调。
  useEffect(() => {
    autoFit();
  }, [autoFit]);

  useEffect(() => {
    if (interactedRef.current) return;
    const shell = shellRef.current;
    if (!shell || typeof ResizeObserver === "undefined") return;
    const observer = new ResizeObserver(() => {
      if (autoFit()) observer.disconnect();
    });
    observer.observe(shell);
    return () => observer.disconnect();
  }, [autoFit]);

  // 换集 = 重新打开一次画布：允许再自动「适应」一次（用户自己的平移不会跨集保留）。
  useEffect(() => {
    if (episodeRef.current === data.episode.id) return;
    episodeRef.current = data.episode.id;
    interactedRef.current = false;
    autoFit();
  }, [autoFit, data.episode.id]);

  // 滚轮语义（C-09，与原型一致）：滚轮 = 平移；Shift = 横向；Ctrl/⌘ = 以光标为中心缩放。
  // React 的 onWheel 在根节点上是 passive，必须用原生监听才能 preventDefault。
  useEffect(() => {
    const shell = shellRef.current;
    if (!shell) return;
    function onWheel(event: WheelEvent) {
      event.preventDefault();
      interactedRef.current = true;
      if (event.ctrlKey || event.metaKey) {
        const rect = shell?.getBoundingClientRect();
        const factor = Math.exp(-event.deltaY * 0.0015);
        setCamera((prev) => {
          const nextZoom = clampZoom(prev.zoom * factor);
          if (nextZoom === prev.zoom) return prev;
          return zoomAtPoint(prev, nextZoom, {
            x: event.clientX - (rect?.left ?? 0),
            y: event.clientY - (rect?.top ?? 0),
          });
        });
        return;
      }
      if (event.shiftKey) {
        const delta = event.deltaY !== 0 ? event.deltaY : event.deltaX;
        setCamera((prev) => ({ ...prev, x: prev.x - delta }));
        return;
      }
      setCamera((prev) => ({
        ...prev,
        x: prev.x - event.deltaX,
        y: prev.y - event.deltaY,
      }));
    }
    shell.addEventListener("wheel", onWheel, { passive: false });
    return () => shell.removeEventListener("wheel", onWheel);
  }, []);

  // 全屏状态只跟随浏览器事件，不重建画布（C-10）。
  useEffect(() => {
    const doc = document as FullscreenDocument;
    function onChange() {
      const current = doc.fullscreenElement ?? doc.webkitFullscreenElement ?? null;
      setIsFullscreen(current === shellRef.current);
    }
    document.addEventListener("fullscreenchange", onChange);
    document.addEventListener("webkitfullscreenchange", onChange);
    return () => {
      document.removeEventListener("fullscreenchange", onChange);
      document.removeEventListener("webkitfullscreenchange", onChange);
    };
  }, []);

  // 右键菜单：点外面关闭（菜单内部的 pointerdown 不关，交给 click 执行动作）。
  useEffect(() => {
    if (!menu) return;
    function onPointerDown(event: PointerEvent) {
      if (menuRef.current?.contains(event.target as Node)) return;
      setMenu(null);
    }
    window.addEventListener("pointerdown", onPointerDown, true);
    return () => window.removeEventListener("pointerdown", onPointerDown, true);
  }, [menu]);

  const toggleFullscreen = useCallback(() => {
    const shell = shellRef.current;
    if (!shell) return;
    const doc = document as FullscreenDocument;
    const current = doc.fullscreenElement ?? doc.webkitFullscreenElement ?? null;
    try {
      if (current) {
        const exit = doc.exitFullscreen ?? doc.webkitExitFullscreen;
        if (exit) void exit.call(doc);
        return;
      }
      const element = shell as FullscreenElement;
      const request = element.requestFullscreen ?? element.webkitRequestFullscreen;
      if (!request) return;
      void request.call(element);
    } catch {
      // 浏览器策略拒绝全屏：画布其余能力不受影响。
    }
  }, []);

  const resetLayout = useCallback(() => {
    clearCanvasLayout(layoutKey);
    layoutRef.current = {};
    setLayout({});
    setImpactNodeId(null);
    interactedRef.current = true;
    // 布局刚清空：按「清空后的图」重算一次适应，别用旧 bounds（拖远过的节点会让视野跑偏）。
    const cleared = buildCanvasGraph({ data, library, layout: {} });
    const next = fitCanvasContent(cleared.bounds, readSize(shellRef.current));
    if (next) setCamera(next);
  }, [data, layoutKey, library]);

  function handlePointerDown(event: ReactPointerEvent<HTMLDivElement>) {
    if (event.button !== 0) return;
    interactedRef.current = true;
    if (isEditableTarget(event.target)) return;
    if ((event.target as HTMLElement).closest(".dp-canvas-queue, .dp-canvas-impact, .dp-canvas-legend, .dp-canvas-jumps, .dp-canvas-controls")) return;
    suppressClickRef.current = false;
    panRef.current = {
      pointerId: event.pointerId,
      startX: event.clientX,
      startY: event.clientY,
      originX: camera.x,
      originY: camera.y,
      moved: false,
    };
    event.currentTarget.setPointerCapture?.(event.pointerId);
    setPanning(true);
    setMenu(null);
  }

  function handlePointerMove(event: ReactPointerEvent<HTMLDivElement>) {
    const nodeDrag = nodeDragRef.current;
    if (nodeDrag) {
      const dx = (event.clientX - nodeDrag.startX) / camera.zoom;
      const dy = (event.clientY - nodeDrag.startY) / camera.zoom;
      if (!nodeDrag.moved && Math.hypot(dx, dy) * camera.zoom > 3) nodeDrag.moved = true;
      if (!nodeDrag.moved) return;
      let next = layoutRef.current;
      for (const item of nodeDrag.items) {
        next = moveNodeInLayout(next, item.id, item.x + dx, item.y + dy);
      }
      layoutRef.current = next;
      setLayout(next);
      return;
    }

    const pan = panRef.current;
    if (!pan) return;
    const dx = event.clientX - pan.startX;
    const dy = event.clientY - pan.startY;
    if (!pan.moved && Math.hypot(dx, dy) > 3) pan.moved = true;
    if (!pan.moved) return;
    setCamera((prev) => ({ ...prev, x: pan.originX + dx, y: pan.originY + dy }));
  }

  function endDrag(event: ReactPointerEvent<HTMLDivElement>) {
    const nodeDrag = nodeDragRef.current;
    if (nodeDrag) {
      nodeDragRef.current = null;
      setDraggingNode(false);
      if (nodeDrag.moved) {
        // 拖完这一次的 click 不能当成「点卡选中」。
        suppressClickRef.current = true;
        setTimeout(() => {
          suppressClickRef.current = false;
        }, 0);
        writeCanvasLayout(layoutKey, layoutRef.current);
      }
      try {
        (event.target as HTMLElement).releasePointerCapture?.(event.pointerId);
      } catch {
        // jsdom / 老浏览器没有指针捕获。
      }
      return;
    }

    const pan = panRef.current;
    if (!pan) return;
    if (pan.moved) {
      suppressClickRef.current = true;
      setTimeout(() => {
        suppressClickRef.current = false;
      }, 0);
    }
    panRef.current = null;
    setPanning(false);
    event.currentTarget.releasePointerCapture?.(event.pointerId);
  }

  function startNodeDrag(event: ReactPointerEvent<HTMLElement>, node: CanvasNode) {
    if (event.button !== 0) return;
    if (node.kind === "label") return;
    event.stopPropagation();
    // 开始拖动 = 用户已经在动布局，提示词浮卡让位（否则会跟着卡片一起飘）。
    setHoveredShotId(null);
    const members = dragMembers(node, graph.nodes);
    nodeDragRef.current = {
      pointerId: event.pointerId,
      startX: event.clientX,
      startY: event.clientY,
      moved: false,
      items: members.map((item) => ({ id: item.id, x: item.x, y: item.y })),
    };
    try {
      event.currentTarget.setPointerCapture?.(event.pointerId);
    } catch {
      // 同上。
    }
    setDraggingNode(true);
  }

  /** 场块 → 同名 location 实体（demo「打开场景资料」；没有同名实体就不显示这一项）。 */
  function sceneLocationOf(node: Extract<CanvasNode, { kind: "scene" }>): LibraryEntity | null {
    const name = node.name.trim();
    if (!name) return null;
    return (
      (library ?? []).find((entity) => entity.kind === "location" && entity.name.trim() === name) ??
      null
    );
  }

  function menuItemsFor(node: CanvasNode): MenuItem[] {
    if (node.kind === "shot") {
      const items: MenuItem[] = [];
      if (onToggleQueueShot) {
        items.push({
          action: "queue",
          label: queued.has(node.shot.id) ? "移出生成队列" : "加入生成队列",
        });
      }
      if (onOpenVersions) items.push({ action: "versions", label: "查看版本" });
      if (onOpenFrames) items.push({ action: "frames", label: "查看首尾帧" });
      if (onOpenStoryboard) items.push({ action: "storyboard", label: "在故事板打开" });
      return items;
    }
    // 首帧 / 出片卡也挂在镜头上：demo 里它们各有自己的菜单项。
    if (node.kind === "keyframe") {
      const items: MenuItem[] = [];
      if (onOpenFrames) items.push({ action: "frames", label: "查看首尾帧" });
      if (onOpenStoryboard) items.push({ action: "storyboard", label: "在故事板打开" });
      return items;
    }
    if (node.kind === "clip") {
      const items: MenuItem[] = [];
      if (onOpenClip) items.push({ action: "clip", label: "打开成片" });
      if (onOpenFrames) items.push({ action: "frames", label: "查看首尾帧" });
      if (onOpenStoryboard) items.push({ action: "storyboard", label: "在故事板打开" });
      return items;
    }
    if (node.kind === "asset") {
      const items: MenuItem[] = [{ action: "impact", label: "查看影响面" }];
      if (onOpenLibrary) items.push({ action: "library", label: "打开资料库" });
      return items;
    }
    if (node.kind === "scene") {
      const items: MenuItem[] = [{ action: "focus", label: "聚焦场块" }];
      if (onOpenLibrary && sceneLocationOf(node)) {
        items.push({ action: "library", label: "打开场景资料" });
      }
      return items;
    }
    return [];
  }

  function openContextMenu(event: ReactMouseEvent<HTMLElement>, node: CanvasNode) {
    if (menuItemsFor(node).length === 0) return;
    const shell = shellRef.current;
    if (!shell) return;
    event.preventDefault();
    event.stopPropagation();
    const rect = shell.getBoundingClientRect();
    // 贴边右键时把菜单收进视口，避免被画布的 overflow: hidden 裁掉。
    const x = Math.max(0, Math.min(event.clientX - rect.left, rect.width - 176));
    const y = Math.max(0, Math.min(event.clientY - rect.top, rect.height - 40 - menuItemsFor(node).length * 30));
    setMenu({ nodeId: node.id, x, y });
  }

  function runMenuAction(action: MenuItem["action"]) {
    const node = menu ? nodeById.get(menu.nodeId) : null;
    setMenu(null);
    if (!node) return;
    const shot = nodeShot(node);
    if (action === "queue" && shot) onToggleQueueShot?.(shot.id);
    if (action === "versions" && shot) onOpenVersions?.(shot.id);
    if (action === "frames" && shot) onOpenFrames?.(shot.id);
    if (action === "clip" && shot) onOpenClip?.(shot.id);
    if (action === "storyboard" && shot) onOpenStoryboard?.(shot.id);
    if (action === "impact" && node.kind === "asset") setImpactNodeId(node.id);
    if (action === "library" && node.kind === "asset") {
      onOpenLibrary?.({ kind: node.entity.kind, id: node.entity.id });
    }
    if (action === "library" && node.kind === "scene") {
      const location = sceneLocationOf(node);
      if (location) onOpenLibrary?.({ kind: "location", id: location.id });
    }
    if (action === "focus" && node.kind === "scene") centerOn(node);
  }

  function handleKeyDown(event: ReactKeyboardEvent<HTMLDivElement>) {
    if (event.metaKey || event.ctrlKey || event.altKey) return;
    const pan = 60;
    switch (event.key) {
      case "+":
      case "=":
        event.preventDefault();
        zoomBy(ZOOM_STEP);
        break;
      case "-":
      case "_":
        event.preventDefault();
        zoomBy(1 / ZOOM_STEP);
        break;
      case "0":
        event.preventDefault();
        fit();
        break;
      case "ArrowLeft":
        event.preventDefault();
        setCamera((prev) => ({ ...prev, x: prev.x + pan }));
        break;
      case "ArrowRight":
        event.preventDefault();
        setCamera((prev) => ({ ...prev, x: prev.x - pan }));
        break;
      case "ArrowUp":
        event.preventDefault();
        setCamera((prev) => ({ ...prev, y: prev.y + pan }));
        break;
      case "ArrowDown":
        event.preventDefault();
        setCamera((prev) => ({ ...prev, y: prev.y - pan }));
        break;
      case "Escape":
        // 全屏退出交给浏览器；这里只收画布内的浮层。
        if (menu) {
          event.preventDefault();
          setMenu(null);
        } else if (impactNodeId) {
          event.preventDefault();
          setImpactNodeId(null);
        }
        break;
      default:
        break;
    }
  }

  if (!hasShots) {
    return (
      <div className="dp-canvas-view" aria-label="画布视图">
        <div className="dp-canvas-empty">
          <EmptyState
            title="这一集还没有镜头"
            description="镜头在故事板里创建；画布是只读视图，会跟着服务端布局更新。"
          />
        </div>
      </div>
    );
  }

  const sceneNodes = graph.nodes.filter(
    (node): node is Extract<CanvasNode, { kind: "scene" }> => node.kind === "scene",
  );
  const shotNodes = graph.nodes.filter(
    (node): node is Extract<CanvasNode, { kind: "shot" }> => node.kind === "shot",
  );
  const assetNodes = graph.nodes.filter(
    (node): node is Extract<CanvasNode, { kind: "asset" }> => node.kind === "asset",
  );
  const derivedNodes = graph.nodes.filter(
    (node): node is Extract<CanvasNode, { kind: "keyframe" | "clip" }> =>
      node.kind === "keyframe" || node.kind === "clip",
  );
  const labelNodes = graph.nodes.filter(
    (node): node is Extract<CanvasNode, { kind: "label" }> => node.kind === "label",
  );
  const firstShot = shotNodes[0];
  const firstAsset = assetNodes[0];
  const menuNode = menu ? nodeById.get(menu.nodeId) : null;
  const menuItems = menuNode ? menuItemsFor(menuNode) : [];
  const menuTitle =
    menuNode?.kind === "shot"
      ? shortId(menuNode.shot.id)
      : menuNode?.kind === "asset"
        ? menuNode.entity.name
        : menuNode?.kind === "scene"
          ? menuNode.name
          : "";

  return (
    <div
      ref={shellRef}
      className={`dp-canvas-view${panning ? " is-dragging" : ""}${draggingNode ? " is-node-dragging" : ""}${impact ? " is-impact" : ""}`}
      role="region"
      aria-label="画布视图"
      tabIndex={0}
      onPointerDown={handlePointerDown}
      onPointerMove={handlePointerMove}
      onPointerUp={endDrag}
      onPointerCancel={endDrag}
      onKeyDown={handleKeyDown}
    >
      <nav className="dp-canvas-jumps" aria-label="跳转场次">
        {sceneNodes.map((node) => (
          <button
            key={`jump-${node.id}`}
            type="button"
            title={`把「${node.name}」移到视口中央`}
            onClick={() => centerOn(node)}
          >
            {node.order === null ? "" : `${pad2(node.order)} `}
            {node.name}
          </button>
        ))}
        {firstShot ? (
          <button type="button" title="回到分镜带" onClick={() => centerOn(firstShot)}>
            分镜 ↑
          </button>
        ) : null}
        {firstAsset ? (
          <button type="button" title="跳到素材带" onClick={() => centerOn(firstAsset)}>
            资产带 ↓
          </button>
        ) : null}
      </nav>

      <div
        className="dp-canvas-world"
        style={{
          transform: `translate(${camera.x}px, ${camera.y}px) scale(${camera.zoom})`,
        }}
      >
        <svg
          className={`dp-canvas-svg${litEdgeIds ? " is-focused" : ""}`}
          style={{ left: svgBox.left, top: svgBox.top }}
          width={svgBox.width}
          height={svgBox.height}
          viewBox={`${svgBox.left} ${svgBox.top} ${svgBox.width} ${svgBox.height}`}
          aria-hidden="true"
          focusable="false"
        >
          <defs>
            <marker
              id="dp-arr-seq"
              viewBox="0 0 8 8"
              refX="7"
              refY="4"
              markerWidth="6"
              markerHeight="6"
              orient="auto"
            >
              <path className="dp-canvas-arrow is-sequence" d="M0 0L8 4L0 8Z" />
            </marker>
            <marker
              id="dp-arr-der"
              viewBox="0 0 8 8"
              refX="7"
              refY="4"
              markerWidth="6"
              markerHeight="6"
              orient="auto"
            >
              <path className="dp-canvas-arrow is-derives" d="M0 0L8 4L0 8Z" />
            </marker>
            <marker
              id="dp-arr-ref"
              viewBox="0 0 8 8"
              refX="7"
              refY="4"
              markerWidth="6"
              markerHeight="6"
              orient="auto"
            >
              <path className="dp-canvas-arrow is-references" d="M0 0L8 4L0 8Z" />
            </marker>
          </defs>
          {graph.edges.map((edge) => {
            const from = nodeById.get(edge.from);
            const to = nodeById.get(edge.to);
            if (!from || !to) return null;
            const classes = ["dp-canvas-edge", `is-${edge.kind}`];
            if (edge.faint) classes.push("is-faint");
            if (litEdgeIds) classes.push(litEdgeIds.has(edge.id) ? "is-lit" : "is-dim");
            return (
              <path
                key={edge.id}
                className={classes.join(" ")}
                data-edge-kind={edge.kind}
                data-edge-id={edge.id}
                data-from={edge.from}
                data-to={edge.to}
                d={edgePath(from, to)}
                markerEnd={`url(#${EDGE_MARKER[edge.kind]})`}
              />
            );
          })}
          {relation ? (
            <g className="dp-canvas-relations-lines" data-rel-anchor={relation.anchorId}>
              {relation.items.map((item) => (
                <path
                  key={item.id}
                  className={`dp-canvas-rel-edge is-${item.kind}`}
                  data-rel-edge-kind={item.kind}
                  d={relationEdgePath(relation.anchor, item)}
                />
              ))}
            </g>
          ) : null}
        </svg>

        {sceneNodes.map((node) => (
          <div
            key={node.id}
            className={`dp-canvas-scene${node.sceneId === UNPLACED_SCENE_ID ? " is-unplaced" : ""}${relationTargetId === node.id ? " is-rel-target" : ""}`}
            data-scene-id={node.sceneId}
            data-node-id={node.id}
            style={rectStyle(node)}
          >
            <button
              type="button"
              className="dp-canvas-scene-tab"
              data-node-id={node.id}
              title={`拖动移动整组「${node.name}」；点击把这一场移到视口中央`}
              onPointerDown={(event) => startNodeDrag(event, node)}
              onClick={() => {
                if (suppressClickRef.current) return;
                centerOn(node);
              }}
              onContextMenu={(event) => openContextMenu(event, node)}
            >
              {node.order === null ? null : <b>{pad2(node.order)}</b>}
              <span>{node.name}</span>
              {node.time ? <em>{node.time}</em> : null}
            </button>
          </div>
        ))}

        {shotNodes.map((node) => {
          const shot = node.shot;
          const meta = SHOT_STATUS_META[shot.status];
          const code = shortId(shot.id);
          const duration = formatDuration(shot.duration);
          const label = `镜头 ${code}${shot.framing ? ` · ${shot.framing}` : ""} · ${meta.label} · ${duration}`;
          const url = shot.keyframe?.url ?? null;
          const placeholder = shot.action.trim().slice(0, 1) || "镜";
          const inQueue = queued.has(shot.id);
          const classes = ["dp-canvas-shot", `is-${meta.tone}`];
          if (inQueue) classes.push("is-queued");
          if (relationTargetId === node.id) classes.push("is-rel-target");
          if (impact) classes.push(impactShotIds.has(shot.id) ? "is-impact" : "is-dimmed");
          return (
            <button
              key={node.id}
              type="button"
              className={classes.join(" ")}
              data-shot-id={shot.id}
              data-node-id={node.id}
              aria-label={label}
              aria-pressed={shot.id === activeShotId}
              aria-describedby={hoveredShotId === shot.id ? SHOT_HOVER_ID : undefined}
              title={`${code}${shot.framing ? ` · ${shot.framing}` : ""} · ${shot.action || meta.label} · 拖动改布局（不写 shot.order）`}
              style={rectStyle(node)}
              onPointerDown={(event) => startNodeDrag(event, node)}
              onMouseEnter={() => showShotHover(shot.id)}
              onMouseLeave={scheduleHoverHide}
              onFocus={() => showShotHover(shot.id)}
              onBlur={(event) => {
                // 焦点落到浮卡里的「复制」时不要收：用户正在操作它。
                const next = event.relatedTarget;
                if (next instanceof Node && hoverPanelRef.current?.contains(next)) return;
                scheduleHoverHide();
              }}
              onClick={(event) => {
                if (suppressClickRef.current) return;
                if (event.shiftKey && onToggleQueueShot) {
                  onToggleQueueShot(shot.id);
                  return;
                }
                setImpactNodeId(null);
                onSelectShot(shot.id);
              }}
              onDoubleClick={() => onOpenStoryboard?.(shot.id)}
              onContextMenu={(event) => openContextMenu(event, node)}
            >
              {url ? (
                <img src={url} alt="" loading="lazy" draggable={false} />
              ) : (
                <span className="dp-cv-ph" aria-hidden="true">
                  <b>{placeholder}</b>
                  <span>{meta.label}</span>
                </span>
              )}

              <span className="dp-cv-top" aria-hidden="true">
                <span className="dp-cv-id">{code}</span>
                {shot.framing ? <span className="dp-cv-framing">{shot.framing}</span> : null}
                <i className={`dp-cv-dot is-${meta.tone}`} />
              </span>

              <span className="dp-cv-foot" aria-hidden="true">
                <span className="dp-cv-dur">{duration}</span>
                {shot.action ? <span className="dp-cv-act">{shot.action}</span> : null}
              </span>
            </button>
          );
        })}

        {derivedNodes.map((node) => {
          const shot = node.shot;
          const code = shortId(shot.id);
          const url = shot.keyframe?.url ?? null;
          const isClip = node.kind === "clip";
          const label = isClip
            ? `镜头 ${code} 出片${shot.clip ? "" : "（未灌入视频）"}`
            : `镜头 ${code} 首帧`;
          return (
            <button
              key={node.id}
              type="button"
              className={`dp-canvas-derived is-${node.kind}${relationTargetId === node.id ? " is-rel-target" : ""}`}
              data-node-id={node.id}
              data-derived-shot={shot.id}
              aria-label={label}
              title={label}
              style={rectStyle(node)}
              onPointerDown={(event) => startNodeDrag(event, node)}
              onClick={() => onSelectShot(shot.id)}
              onContextMenu={(event) => openContextMenu(event, node)}
            >
              {url ? (
                <img src={url} alt="" loading="lazy" draggable={false} />
              ) : (
                <span className="dp-cv-derived-ph" aria-hidden="true">
                  {isClip ? "▷" : "帧"}
                </span>
              )}
              <span className="dp-cv-derived-label">
                {isClip ? (shot.clip ? "出片" : "未灌入") : "首帧"}
              </span>
            </button>
          );
        })}

        {assetNodes.map((node) => {
          const entity = node.entity;
          const url = libraryRefUrl(entity);
          const kindLabel = CANVAS_ASSET_KIND_LABEL[entity.kind];
          const classes = ["dp-canvas-asset", `is-${entity.kind}`];
          if (relationTargetId === node.id) classes.push("is-rel-target");
          if (impact) classes.push(impactNodeId === node.id ? "is-impact-source" : "is-dimmed");
          return (
            <button
              key={node.id}
              type="button"
              className={classes.join(" ")}
              data-node-id={node.id}
              data-asset-id={entity.id}
              data-asset-kind={entity.kind}
              aria-label={`${kindLabel} ${entity.name} · 引用 ${node.refCount} 镜`}
              aria-pressed={impactNodeId === node.id}
              title={`${kindLabel} · ${entity.name} · 被 ${node.refCount} 个镜头引用`}
              style={rectStyle(node)}
              onPointerDown={(event) => startNodeDrag(event, node)}
              onClick={() => {
                if (suppressClickRef.current) return;
                setImpactNodeId(node.id);
              }}
              onContextMenu={(event) => openContextMenu(event, node)}
            >
              {url ? (
                <img src={url} alt="" loading="lazy" draggable={false} />
              ) : (
                <span className="dp-cv-asset-ph" aria-hidden="true">
                  {[...entity.name.trim()][0] ?? "·"}
                </span>
              )}
              <span className="dp-cv-asset-meta">
                <span className="dp-cv-asset-name">{entity.name}</span>
                <span className="dp-cv-asset-count" title={`引用 ${node.refCount} 镜`}>
                  {node.refCount}
                </span>
              </span>
            </button>
          );
        })}

        {labelNodes.map((node) => (
          <div key={node.id} className="dp-canvas-band-label" style={rectStyle(node)}>
            {node.text}
          </div>
        ))}

        {/* hover 提示词浮卡（原型 shot-cover-hover）：数据是投影里的 promptPreview。 */}
        {hoverPanel ? (
          <div
            ref={hoverPanelRef}
            id={SHOT_HOVER_ID}
            className="dp-canvas-shot-hover"
            role="group"
            aria-label="镜头提示词预览"
            data-hover-shot={hoverPanel.shot.id}
            style={{
              left: hoverPanel.left,
              top: hoverPanel.top,
              width: SHOT_HOVER_WIDTH,
              // 屏幕尺寸 = SHOT_HOVER_WIDTH（世界缩放被这一层抵消）。
              transform: `scale(${hoverPanel.scale})`,
              transformOrigin: "left top",
            }}
            onMouseEnter={cancelHoverHide}
            onMouseLeave={scheduleHoverHide}
            onPointerDown={(event) => event.stopPropagation()}
          >
            <div className="dp-cv-hover-head">
              <b>{shortId(hoverPanel.shot.id)}</b>
              {hoverPanel.shot.framing ? <span>{hoverPanel.shot.framing}</span> : null}
              <span>{formatDuration(hoverPanel.shot.duration)}</span>
              <i className={`dp-cv-dot is-${SHOT_STATUS_META[hoverPanel.shot.status].tone}`} />
              <em>{SHOT_STATUS_META[hoverPanel.shot.status].label}</em>
            </div>
            {hoverPanel.shot.action ? (
              <p className="dp-cv-hover-action">{hoverPanel.shot.action}</p>
            ) : null}
            <div className="dp-cv-hover-label">PROMPT · 提示词</div>
            {hoverPanel.shot.promptPreview ? (
              <pre className="dp-cv-hover-prompt">{hoverPanel.shot.promptPreview}</pre>
            ) : (
              <p className="dp-cv-hover-empty">
                这一镜还没有提示词预览：服务端投影没有算出来（老数据 / 没配资料库）。
              </p>
            )}
            <div className="dp-cv-hover-foot">
              <span className="dp-cv-hover-hint">Shift+点击入队 · 双击回故事板</span>
              <button
                type="button"
                className="dp-cv-hover-copy"
                aria-label="复制提示词"
                disabled={!hoverPanel.shot.promptPreview}
                onClick={() => void copyPrompt(hoverPanel.shot.promptPreview ?? "")}
              >
                {copyState === "done" ? "已复制" : copyState === "fail" ? "复制失败" : "复制"}
              </button>
            </div>
          </div>
        ) : null}

        {/* C-06：选中镜头右侧一列半透明缩略图（只有图，不带文字）。 */}
        {relation ? (
          <div
            className="dp-canvas-relations"
            data-rel-anchor={relation.anchorId}
            aria-label="关联快捷视图"
            style={{
              left: relation.rect.x,
              top: relation.rect.y,
              width: relation.rect.w,
              height: relation.rect.h,
            }}
          >
            {relation.items.map((item) => (
              <button
                key={item.id}
                type="button"
                className={`dp-canvas-rel is-${item.kind}`}
                data-rel-kind={item.kind}
                data-rel-node-id={item.nodeId}
                aria-label={`跳到${item.label}`}
                title={`${item.label} · 滚到视口中央`}
                style={{
                  left: 0,
                  top: item.y - relation.rect.y,
                  width: item.w,
                  height: item.h,
                }}
                onClick={(event) => {
                  event.stopPropagation();
                  jumpToRelation(item);
                }}
              >
                {item.url ? (
                  <img src={item.url} alt="" loading="lazy" draggable={false} />
                ) : (
                  <span className="dp-canvas-rel-ph" aria-hidden="true">
                    {item.placeholder}
                  </span>
                )}
              </button>
            ))}
          </div>
        ) : null}
      </div>

      {queueSlot ? <div className="dp-canvas-queue">{queueSlot}</div> : null}

      {impact ? (
        <div className="dp-canvas-impact" role="region" aria-label="影响面">
          <span className="dp-ci-kind">{CANVAS_ASSET_KIND_LABEL[impact.target.kind]}</span>
          <b>{impact.target.name}</b>
          <span className="dp-ci-stat">
            影响 <b>{impact.shotIds.length}</b> 镜 · <b>{impact.keyframeCount}</b> 关键帧 ·{" "}
            <b>{impact.sceneCount}</b> 场
          </span>
          {onOpenLibrary ? (
            <button
              type="button"
              onClick={() =>
                onOpenLibrary({ kind: impact.target.kind, id: impact.target.entityId })
              }
            >
              打开资料库
            </button>
          ) : null}
          <button
            type="button"
            className="dp-ci-x"
            aria-label="清除影响高亮"
            title="清除影响高亮"
            onClick={() => setImpactNodeId(null)}
          >
            ×
          </button>
        </div>
      ) : null}

      {menu && menuNode && menuItems.length > 0 ? (
        <div
          ref={menuRef}
          className="dp-canvas-ctx"
          role="menu"
          aria-label="节点菜单"
          style={{ left: menu.x, top: menu.y }}
          onPointerDown={(event) => event.stopPropagation()}
        >
          <div className="dp-ctx-title">{menuTitle}</div>
          {menuItems.map((item) => (
            <button
              key={item.action}
              type="button"
              role="menuitem"
              onClick={() => runMenuAction(item.action)}
            >
              {item.label}
            </button>
          ))}
        </div>
      ) : null}

      <div className="dp-canvas-legend">
        {onOpenShortcuts ? (
          <button
            type="button"
            className="dp-canvas-shortcuts"
            title="画布的鼠标与键盘操作"
            onClick={onOpenShortcuts}
          >
            ⌘ 快捷操作
          </button>
        ) : null}
        {LEGEND.map((item) => (
          <span key={item.tone}>
            <i className={`is-${item.tone}`} />
            {item.label}
          </span>
        ))}
        <span className="dp-canvas-legend-sep">
          <i className="dp-legend-line is-sequence" />
          顺序
        </span>
        <span>
          <i className="dp-legend-line is-derives" />
          派生
        </span>
        <span>
          <i className="dp-legend-line is-references" />
          引用
        </span>
        <span className="dp-canvas-hint">
          {activeShot ? `已选 ${shortId(activeShot.id)} · 绿色为引用` : "点一个镜头看引用关系"}
        </span>
        <span className="dp-canvas-hint">滚轮平移 · Shift 横移 · ⌘/Ctrl 滚轮缩放</span>
      </div>

      <div className="dp-canvas-controls">
        <output className="dp-canvas-zoom" aria-label="当前缩放">
          {Math.round(camera.zoom * 100)}%
        </output>
        <button type="button" aria-label="放大" title="放大（+）" onClick={() => zoomBy(ZOOM_STEP)}>
          +
        </button>
        <button
          type="button"
          aria-label="缩小"
          title="缩小（−）"
          onClick={() => zoomBy(1 / ZOOM_STEP)}
        >
          −
        </button>
        <button type="button" aria-label="适应视口" title="适应画面（0）" onClick={fit}>
          适应
        </button>
        <button
          type="button"
          aria-label={isFullscreen ? "退出全屏" : "全屏"}
          title={isFullscreen ? "退出全屏（Esc）" : "全屏"}
          aria-pressed={isFullscreen}
          onClick={toggleFullscreen}
        >
          {isFullscreen ? "⤡" : "⛶"}
        </button>
        <button
          type="button"
          aria-label="恢复自动布局"
          title="恢复自动布局（只清本地布局，不改镜头顺序）"
          onClick={resetLayout}
        >
          ↺
        </button>
      </div>
    </div>
  );
}
